import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

export type DsBadgeTone    = 'brand' | 'focus' | 'neutral';
export type DsBadgeVariant = 'solid' | 'outline';
export type DsBadgeSize    = 'sm' | 'md';

@Component({
  selector: 'ds-badge',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="ds-badge" [class]="classes()">
      <ng-content select="[ds-badge-icon]" />
      <span class="ds-badge__label"><ng-content /></span>
    </span>
  `,
  styles: [`
    :host { display: inline-flex; }

    .ds-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-family: var(--f-mono);
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      border-radius: var(--r-full);
      white-space: nowrap;
    }
    .ds-badge__label { display: inline-flex; align-items: center; }
    [ds-badge-icon] { display: inline-flex; align-items: center; flex: 0 0 auto; }

    /* ── Sizes ────────────────────────────────────────────────── */
    .ds-badge--sm { font-size: 10px; padding: 4px 10px; min-height: 22px; }
    .ds-badge--md { font-size: 11px; padding: 6px 12px; min-height: 26px; }

    /* ── Solid variants ───────────────────────────────────────── */
    /* #CC2831 = R90 — éviter c-accent en texte/fond surfaces claires */
    .ds-badge--solid.ds-badge--brand {
      background: #CC2831;
      color: #ffffff;
    }
    :where(.dark) .ds-badge--solid.ds-badge--brand {
      background: #FF6B6B;
      color: #0a0a0a;
    }

    .ds-badge--solid.ds-badge--focus {
      background: var(--c-focus);
      color: #ffffff;
    }

    .ds-badge--solid.ds-badge--neutral {
      background: var(--on-surface);
      color: var(--surface);
    }

    /* ── Outline variants ─────────────────────────────────────── */
    .ds-badge--outline {
      background: transparent;
      border: 1.5px solid currentColor;
    }
    .ds-badge--outline.ds-badge--brand   { color: #CC2831; }
    :where(.dark) .ds-badge--outline.ds-badge--brand   { color: #FF6B6B; }
    .ds-badge--outline.ds-badge--focus   { color: var(--c-focus); }
    .ds-badge--outline.ds-badge--neutral { color: var(--on-surface-muted); }

    /* ── On inverse surfaces (cards inverse/brand) ───────────── */
    :host-context(.ds-card--inverse) .ds-badge--outline.ds-badge--neutral,
    :host-context(.ds-card--brand)   .ds-badge--outline.ds-badge--neutral {
      color: rgba(255,255,255,0.75);
    }
    :host-context(.ds-card--inverse) .ds-badge--solid.ds-badge--brand,
    :host-context(.ds-card--brand)   .ds-badge--solid.ds-badge--brand {
      background: #FF6B6B;
      color: #0a0a0a;
    }
  `],
})
export class DsBadgeComponent {
  readonly tone    = input<DsBadgeTone>('neutral');
  readonly variant = input<DsBadgeVariant>('solid');
  readonly size    = input<DsBadgeSize>('sm');

  readonly classes = computed(() => [
    `ds-badge--${this.variant()}`,
    `ds-badge--${this.tone()}`,
    `ds-badge--${this.size()}`,
  ].join(' '));
}
